import React, { useState, useEffect, FC } from 'react';
import _ from 'lodash';
import { useTranslation } from '@wix/fe-essentials-standalone';
import {
  Box,
  Text,
  TextButton,
  Heading,
  Divider,
  StarsRatingBar,
  starRatingBarValue,
} from '@wix/design-system';
import { ExternalLink } from '@wix/wix-ui-icons-common';
import { Service } from '@wix/ambassador-category-services/types';
import { ReviewData } from '@common/types';
import { MAX_CHARS_IN_REVIEW } from '@utils/constants';

import ReplyReview from '../ReplyReview ';
import WriteReplyReview from '../WriteReplyReview/WriteReplyReview';
import ShowMoreText from '../../ShowMoreText';
import s from './Review.module.scss';

type ReviewProps = {
  review: ReviewData;
  urlCtaKey: string;
  services?: Service[];
  isMyProfile?: boolean;
  writeReplyToReview: (
    id: string,
    replyBody: string,
    revision: Long.Long | undefined,
  ) => Promise<void>;
};

const Review: FC<ReviewProps> = ({
  review,
  isMyProfile,
  services,
  urlCtaKey,
  writeReplyToReview,
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const [serviceName, setServiceName] = useState<string>('');
  const [overallScore, setOverallScore] = useState<number>(0);

  useEffect(() => {
    if (services && review) {
      const service = services.find((item) => item?.id === review.serviceId);
      if (_.get(service, 'displayKey')) {
        setServiceName(t(`explore.services.${service?.displayKey}`));
      }
    }
    setOverallScore(review?.overallScore || review?.score?.average || 0);
  }, [services, review, t]);
  return review ? (
    <Box
      display="block"
      dataHook="review-container"
      overflow="hidden"
      padding="2px"
      maxWidth="828px"
      width="100%"
    >
      {!_.isNil(review?.overallScore || review?.score?.average) && (
        <Box marginBottom="SP1" verticalAlign="middle">
          <StarsRatingBar
            value={Math.round(overallScore) as starRatingBarValue}
            size="small"
            readOnly
          />
          <Box marginLeft="SP1">
            <Heading size="extraTiny" className={s.colorD20}>
              {Intl.NumberFormat(language, {
                maximumFractionDigits: 1,
                minimumFractionDigits: 1,
              }).format(overallScore)}
            </Heading>
          </Box>
        </Box>
      )}
      <Box paddingBottom="SP2" width="100%" verticalAlign="middle">
        {review.clientName && (
          <>
            <Text
              size="tiny"
              weight="thin"
              secondary
              dataHook="review-client-name"
            >{`${review.clientName}`}</Text>
            <Box display="inline-block" height="16px" margin="0 SP1">
              <Divider direction="vertical" />
            </Box>
          </>
        )}
        {review.date && (
          <>
            <Text size="tiny" weight="thin" secondary dataHook="review-date">
              {review.date &&
                Intl.DateTimeFormat(language, {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric',
                }).format(new Date(review.date))}
            </Text>
          </>
        )}
        {review.url && (
          <>
            <Box display="inline-block" height="16px" margin="0 SP1">
              <Divider direction="vertical" />
            </Box>
            <TextButton
              className={s.colorD20}
              dataHook="review-url"
              as="a"
              skin="dark"
              size="tiny"
              weight="thin"
              underline="always"
              href={review.url}
              suffixIcon={<ExternalLink />}
              target="_blank"
            >
              {t(urlCtaKey)}
            </TextButton>
          </>
        )}
      </Box>
      <Box display="block" paddingBottom="SP2">
        <ShowMoreText
          dataHookPrefix="review-"
          originalText={review?.text}
          maxChars={MAX_CHARS_IN_REVIEW}
        />
      </Box>
      {serviceName && (
        <Text
          size="small"
          weight="thin"
          light
          secondary
          tagName="div"
          dataHook="review-service-name"
        >
          {t('profile-page-review-service-text', { serviceName })}
        </Text>
      )}
      {review.reply ? (
        <Box display="block" marginTop="SP4">
          <ReplyReview reply={review.reply} />
        </Box>
      ) : isMyProfile ? (
        <Box display="block" marginTop="SP4">
          <WriteReplyReview
            review={review}
            writeReplyToReview={writeReplyToReview}
          />
        </Box>
      ) : null}
    </Box>
  ) : (
    <></>
  );
};

export default Review;
